import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Clock } from "lucide-react";
import { toast } from "sonner";
import ImageUpload from "@/components/ImageUpload";
import MarkdownEditor from "@/components/MarkdownEditor";
import PublicLinkButton from "@/components/PublicLinkButton";
import { useLang } from "@/context/LangContext";

const CATS = ["Innovation", "Technologie", "Startup", "Marché", "Digital", "Communiqué", "Événements", "Interviews"];

export default function MemberNews() {
  const { t } = useLang();
  const [news, setNews] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", excerpt: "", body: "", category: "Innovation", image: "", tags: "", source_lang: "fr" });
  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const load = () => api.get("/my/news").then((r) => setNews(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
      await api.post("/news", { ...form, tags });
      setForm({ title: "", excerpt: "", body: "", category: "Innovation", image: "", tags: "", source_lang: "fr" });
      setOpen(false);
      toast.success("Actualité soumise pour validation par l'équipe La WineTech");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const badge = (s) => ({
    pending: "bg-amber-50 text-amber-700",
    published: "bg-green-50 text-green-700",
    draft: "bg-neutral-100 text-neutral-700",
    rejected: "bg-red-50 text-red-700",
  }[s] || "bg-neutral-100 text-neutral-700");

  return (
    <PortalLayout sidebar="member">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">{t("portal_membernews_01")}</h1>
          <p className="mt-1 text-neutral-600">{t("portal_membernews_02")}<span className="font-semibold text-wine-700">{t("portal_membernews_03")}</span> avant publication.</p>
        </div>
        <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-2 rounded-full bg-wine-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="news-new-button">
          {open ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />} {open ? "Fermer" : "Nouvelle actualité"}
        </button>
      </div>

      {open && (
        <div className="mt-6 rounded-2xl border border-wine-100 bg-amber-50/40 p-4 text-xs text-amber-800">
          <Clock className="mr-1 inline h-3.5 w-3.5" />{t("portal_membernews_04")}<strong>{t("portal_membernews_05")}</strong>.
        </div>
      )}
      {open && (
        <form onSubmit={submit} className="mt-3 grid gap-4 rounded-2xl border border-wine-100 bg-white p-6 md:grid-cols-2">
          <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="news-form-title" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="news-form-category">{CATS.map((c) => <option key={c}>{c}</option>)}</select>
          <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="Tags (virgules)" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm" data-testid="news-form-tags" />
          {/* Sprint 92ao — Author declares source language → translated into all platform languages for display */}
          <label className="rounded-xl border border-pink-200 bg-pink-50/40 px-3 py-2 text-xs font-semibold text-pink-700 md:col-span-2" data-testid="news-form-source-lang-wrapper">
            🌍 Langue de rédaction (traduction automatique dans les autres langues)
            <select
              value={form.source_lang}
              onChange={(e) => setForm({ ...form, source_lang: e.target.value })}
              className="mt-1 w-full rounded-lg border border-pink-200 bg-white px-3 py-1.5 text-sm font-normal text-neutral-700"
              data-testid="news-form-source-lang"
            >
              <option value="fr">🇫🇷 Français</option>
              <option value="en">🇬🇧 English</option>
              <option value="es">🇪🇸 Español</option>
              <option value="it">🇮🇹 Italiano</option>
              <option value="pt">🇵🇹 Português</option>
              <option value="de">🇩🇪 Deutsch</option>
            </select>
          </label>
          <div className="md:col-span-2">
            <ImageUpload value={form.image} onChange={(url) => setForm({ ...form, image: url })} label={t("portal_membernews_90")} testId="member-news-image" />
          </div>
          <textarea required rows={2} value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Extrait" className="rounded-xl border border-neutral-200 px-4 py-2.5 text-sm md:col-span-2" data-testid="news-form-excerpt" />
          <div className="md:col-span-2">
            <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Contenu (Markdown)</label>
            <div className="mt-1"><MarkdownEditor value={form.body} onChange={(v) => setForm({ ...form, body: v })} rows={8} testId="member-news-body" /></div>
          </div>
          <div className="md:col-span-2">
            <button type="submit" className="rounded-full bg-wine-500 px-6 py-2.5 text-sm font-semibold text-white hover:bg-wine-600" data-testid="news-form-submit">{t("portal_membernews_06")}</button>
          </div>
        </form>
      )}

      <div className="mt-8 overflow-hidden rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-wine-50/40 text-xs uppercase tracking-wider text-neutral-500">
            <tr><th className="px-5 py-3 text-left">Image</th><th className="px-5 py-3 text-left">Titre</th><th className="px-5 py-3 text-left">{t("portal_membernews_07")}</th><th className="px-5 py-3 text-left">Statut</th><th className="px-5 py-3 text-left">Date</th><th className="px-5 py-3 text-right">Public</th></tr>
          </thead>
          <tbody>
            {news.map((n) => {
              const src = n.image?.startsWith("/api/") ? `${BACKEND}${n.image}` : n.image;
              return (
                <tr key={n.id} className="border-t border-neutral-100" data-testid={`my-news-row-${n.id}`}>
                  <td className="px-5 py-3">{src && <img src={src} alt="" className="h-12 w-16 rounded-lg object-cover" />}</td>
                  <td className="px-5 py-3 font-medium text-neutral-900">{n.title}</td>
                  <td className="px-5 py-3 text-neutral-600">{n.category}</td>
                  <td className="px-5 py-3"><span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${badge(n.status)}`}>{n.status}</span></td>
                  <td className="px-5 py-3 text-neutral-500">{n.created_at && new Date(n.created_at).toLocaleDateString("fr")}</td>
                  <td className="px-5 py-3 text-right">{n.status === "published" && <PublicLinkButton slug={n.slug} type="news" compact />}</td>
                </tr>
              );
            })}
            {news.length === 0 && <tr><td colSpan={6} className="px-5 py-10 text-center text-sm text-neutral-500">{t("portal_membernews_08")}</td></tr>}
          </tbody>
        </table>
      </div>
    </PortalLayout>
  );
}
